import React, { useEffect, useState } from "react";
import { Plus, Trash2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { relativeTime } from "@/lib/utils";

const inputCls = "w-full px-3 py-2 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm transition-colors";

function StepEditor({ step, onChange, onRemove, testId }) {
  return (
    <div className="rounded-xl border border-neutral-200 p-3 space-y-2 bg-neutral-50/50" data-testid={testId}>
      <div className="flex gap-2">
        <input data-testid={`${testId}-title`} value={step.title} onChange={(e) => onChange({ ...step, title: e.target.value })} placeholder="Step title" className={`${inputCls} font-semibold`} />
        <button type="button" data-testid={`${testId}-remove`} onClick={onRemove} className="p-2 rounded-full text-red-500 hover:bg-red-50" aria-label="Remove step"><Trash2 size={15} /></button>
      </div>
      <textarea data-testid={`${testId}-body`} rows={2} value={step.body} onChange={(e) => onChange({ ...step, body: e.target.value })} placeholder="Explanation shown to investors" className={inputCls} />
    </div>
  );
}

function SectionEditor({ sec, si, setSec, removeSec }) {
  const setStep = (i, st) => setSec({ ...sec, steps: sec.steps.map((x, j) => (j === i ? st : x)) });
  return (
    <div className="soft-card p-4 space-y-3" data-testid={`guide-edit-section-${si}`}>
      <div className="flex gap-2">
        <input data-testid={`guide-edit-section-${si}-title`} value={sec.title} onChange={(e) => setSec({ ...sec, title: e.target.value })} className={`${inputCls} font-bold`} />
        <button type="button" onClick={removeSec} className="p-2 rounded-full text-red-500 hover:bg-red-50" aria-label="Remove section"><Trash2 size={15} /></button>
      </div>
      {sec.steps.map((st, i) => (
        <StepEditor key={st.id || i} step={st} testId={`guide-edit-step-${si}-${i}`} onChange={(v) => setStep(i, v)} onRemove={() => setSec({ ...sec, steps: sec.steps.filter((_, j) => j !== i) })} />
      ))}
      <button type="button" data-testid={`guide-edit-add-step-${si}`} onClick={() => setSec({ ...sec, steps: [...sec.steps, { title: "", body: "" }] })} className="text-xs font-semibold text-propera inline-flex items-center gap-1 hover:underline"><Plus size={13} /> Add step</button>
    </div>
  );
}

export default function AdminGuideTab() {
  const [guides, setGuides] = useState([]);
  const [country, setCountry] = useState("Kenya");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data } = await api.get("/guide");
    setGuides(data.countries);
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const g = guides.find((x) => x.country === country);
    if (g) setDraft(JSON.parse(JSON.stringify({ intro: g.intro, sections: g.sections, updated_at: g.updated_at, customised: g.customised })));
  }, [guides, country]);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/admin/guide/${country}`, { intro: draft.intro, sections: draft.sections });
      toast.success(`${country} guide saved`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  async function reset() {
    if (!window.confirm(`Reset the ${country} guide to Propera's default content?`)) return;
    await api.post(`/admin/guide/${country}/reset`);
    toast.success("Guide reset to default");
    await load();
  }

  if (!draft) return <p className="text-sm text-neutral-500">Loading…</p>;
  const setSec = (i, s) => setDraft({ ...draft, sections: draft.sections.map((x, j) => (j === i ? s : x)) });
  return (
    <div data-testid="admin-guide-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {guides.map((g) => (
          <button key={g.country} data-testid={`admin-guide-country-${g.country}`} onClick={() => setCountry(g.country)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${country === g.country ? "bg-propera text-white border-propera" : "bg-white border-neutral-200"}`}>{g.country}</button>
        ))}
        <span className="ml-auto text-xs text-neutral-500">{draft.customised ? `Edited ${relativeTime(draft.updated_at)}` : "Using default content"}</span>
      </div>
      <div className="mt-5 space-y-4">
        <div>
          <label className="text-xs font-semibold text-neutral-700">Introduction</label>
          <textarea data-testid="guide-edit-intro" rows={3} value={draft.intro} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} className={`${inputCls} mt-1.5`} />
        </div>
        {draft.sections.map((sec, i) => (
          <SectionEditor key={sec.id || i} sec={sec} si={i} setSec={(s) => setSec(i, s)} removeSec={() => setDraft({ ...draft, sections: draft.sections.filter((_, j) => j !== i) })} />
        ))}
        <button type="button" data-testid="guide-edit-add-section" onClick={() => setDraft({ ...draft, sections: [...draft.sections, { title: "New section", steps: [] }] })} className="text-sm font-semibold text-propera inline-flex items-center gap-1 hover:underline"><Plus size={14} /> Add section</button>
      </div>
      <div className="sticky bottom-4 mt-6 flex justify-end gap-2">
        <button data-testid="guide-edit-reset" onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-neutral-200 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50"><RotateCcw size={14} /> Reset to default</button>
        <button data-testid="guide-edit-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-propera hover:bg-propera-hover text-white text-sm font-semibold disabled:opacity-60 shadow-lg"><Save size={14} /> {saving ? "Saving…" : "Save guide"}</button>
      </div>
    </div>
  );
}
